import * as THREE from "three";
import { Line2 } from "three/examples/jsm/lines/Line2.js";
import { LineGeometry } from "three/examples/jsm/lines/LineGeometry.js";
import { LineMaterial, type LineMaterialParameters } from "three/examples/jsm/lines/LineMaterial.js";
import { LineSegments2 } from "three/examples/jsm/lines/LineSegments2.js";
import { LineSegmentsGeometry } from "three/examples/jsm/lines/LineSegmentsGeometry.js";
import { HIGHLIGHTABLE_LAYER, RAYCASTABLE_LAYER, SELECTABLE_LAYER, SNAPPABLE_LAYER } from "../../../layers";

/**
 * Line2 defaults `resolution` to the renderer viewport, which the renderer services set in DEVICE
 * pixels — that would silently redefine `linewidth` as device pixels (2px reads as 1 CSS px on a 2x
 * display). Feed it the canvas CSS size instead, matching three's documented units. It has to happen
 * here: onBeforeRender runs immediately before the draw call, so a value written earlier in the
 * frame would be overwritten.
 */
function trackCanvasResolution(line: LineSegments2): void {
	line.onBeforeRender = (renderer) => {
		line.material.resolution.set(renderer.domElement.clientWidth, renderer.domElement.clientHeight);
	};
}

/**
 * A polyline with a real, pixel-denominated thickness. `THREE.Line` ignores `linewidth` on every
 * mainstream driver, which leaves a 1px hairline — unusable over a map.
 */
function createThickLine({ materialDefinition }: { materialDefinition: LineMaterialParameters }): Line2 {
	const line = new Line2(new LineGeometry(), new LineMaterial(materialDefinition));

	line.layers.enable(RAYCASTABLE_LAYER);
	line.layers.enable(SNAPPABLE_LAYER);
	line.layers.enable(HIGHLIGHTABLE_LAYER);
	line.layers.enable(SELECTABLE_LAYER);

	line.frustumCulled = false;
	trackCanvasResolution(line);

	return line;
}

/** Disconnected thick segments, for overlays drawn on top of an outline. No picking layers. */
function createThickLineSegments({ materialDefinition }: { materialDefinition: LineMaterialParameters }): LineSegments2 {
	const lineSegments = new LineSegments2(new LineSegmentsGeometry(), new LineMaterial(materialDefinition));

	lineSegments.frustumCulled = false;
	trackCanvasResolution(lineSegments);

	return lineSegments;
}

/**
 * Rebuilds a thick line's geometry. `LineGeometry` reads the positions as a chain,
 * `LineSegmentsGeometry` as pairs — which is exactly the outline vs. overlay split.
 *
 * Both are fed through `setPositions` with a flat array: `setFromPoints` exists only on
 * `LineGeometry`, so on a `LineSegmentsGeometry` it would silently resolve to
 * `BufferGeometry.setFromPoints` and write a plain `position` attribute, leaving the instanced
 * attributes the shader and `computeLineDistances` need undefined.
 */
function setThickLinePoints({ line, points }: { line: LineSegments2; points: ReadonlyArray<THREE.Vector3> }): void {
	line.geometry.dispose();
	line.geometry = line instanceof Line2 ? new LineGeometry() : new LineSegmentsGeometry();

	// Fewer than two points leaves nothing to draw, and `setPositions` would build an empty
	// instanced buffer.
	if (points.length < 2) {
		line.visible = false;
		return;
	}

	const positions: Array<number> = [];

	for (const point of points) {
		positions.push(point.x, point.y, point.z);
	}

	line.geometry.setPositions(positions);
	line.visible = true;

	// Feeds the per-segment distance attributes; without it the dash shader has nothing to march along.
	if (line.material.dashed === true) {
		line.computeLineDistances();
	}
}

function createLine({
	materialDefinition,
}: {
	materialDefinition: THREE.LineBasicMaterialParameters;
}): THREE.Line<THREE.BufferGeometry, THREE.LineBasicMaterial> {
	const line = new THREE.Line(new THREE.BufferGeometry(), new THREE.LineBasicMaterial(materialDefinition));

	line.layers.enable(RAYCASTABLE_LAYER);
	line.layers.enable(SNAPPABLE_LAYER);
	line.layers.enable(HIGHLIGHTABLE_LAYER);
	line.layers.enable(SELECTABLE_LAYER);

	line.frustumCulled = false;

	return line;
}

const VERTEX_HANDLE_RADIUS = 0.7;
const VERTEX_HANDLE_SEGMENTS = 16;

function createVertexHandle({ materialDefinition }: { materialDefinition: THREE.MeshBasicMaterialParameters }) {
	const handle = new THREE.Mesh(
		new THREE.SphereGeometry(VERTEX_HANDLE_RADIUS, VERTEX_HANDLE_SEGMENTS, VERTEX_HANDLE_SEGMENTS),
		new THREE.MeshBasicMaterial(materialDefinition),
	);

	handle.frustumCulled = false;
	handle.renderOrder = 999;

	return handle;
}

function disposeVertex(handle: THREE.Mesh<THREE.SphereGeometry, THREE.MeshBasicMaterial>): void {
	for (const child of handle.children) {
		if (child instanceof THREE.LineSegments) {
			child.geometry.dispose();

			if (child.material instanceof THREE.Material) {
				child.material.dispose();
			}
		}
	}

	handle.clear();
	handle.geometry.dispose();
	handle.material.dispose();
}

export { createLine, createThickLine, createThickLineSegments, createVertexHandle, disposeVertex, setThickLinePoints };
